import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import AutoComplete from "../../components/common/AutoComplete";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDateTimePicker from "../../components/common/CustomDateTimePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import {
  fetchCurrency,
  useCurrenciesQuery,
} from "../../services/CurrencyService";
import {
  fetchCustomerBillById,
  useCustomerBillQuery,
} from "../../services/CustomerBillService";
import {
  AccountyEnumType,
  addPaymentApi,
  exportPayment,
  fetchPaymentType,
  IPayment,
  PaymentTypeEnum,
  usePaymentQuery,
} from "../../services/PaymentService";
import {
  fetchProfile,
  useProfilesDropdown,
  useProfilesQuery,
} from "../../services/ProfilesService";
import {
  fetchProviderBillById,
  useProviderBillQuery,
} from "../../services/ProviderBillService";
import { DateRange, IField, Params } from "../../types/table/interfaces.type";
import { useAccountsQuery } from "../../services/AccountService";

import History from "../../components/History";
import Actions from "../../components/common/Actions";
import { clear } from "console";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface Option {
  label: string;
  value: string;
}

const PaymentPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<IPayment & DateRange>
  >({
    search: "",
    type: undefined,
    paymentType: undefined,
    profileId: "",
  });

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [profileOptions, setProfileOptions] = useState<Option[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<Option[]>([]);
  const [filters, setFilters] = useState<Params<IPayment>>({});
  const [profileMap, setProfileMap] = useState<
    Record<
      string,
      { name: string; accountManagerEmail: string; currencyId: string }
    >
  >({});
  const [currencyMap, setCurrencyMap] = useState<Record<number, string>>({});
  const [filterDeleted, setFilterDeleted] = useState(false);

  const { data: dataProfile } = useProfilesQuery();
  const { data: currencyOption } = useCurrenciesQuery();
  const { data: dataProfiledropdown = [] } = useProfilesDropdown(filterDeleted);
  useEffect(() => {
    if (dataProfile) {
      setProfileOptions(dataProfile.profileOptions);
      setProfileMap(dataProfile.profileMap);
    }
    if (currencyOption) {
      const map = currencyOption.reduce<Record<number, string>>((acc, curr) => {
        acc[curr.id] = curr.name;
        return acc;
      }, {});
      setCurrencyMap(map);
    }
  }, [currencyOption, queryClient, dataProfile]);

  const dialogRef = useRef<PaymentDialogRef>(null);
  const handleOpenDialog = (mode: "add" | "history", id?: string) => {
    dialogRef.current?.openDialog(mode, id);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const paymentTypes = await fetchPaymentType();

        const paymentTypeAsOptions = paymentTypes.map((type) => ({
          label: type,
          value: PaymentTypeEnum[type as keyof typeof PaymentTypeEnum],
        }));

        setPaymentOptions(paymentTypeAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      type: filters.type,
      paymentType: filters.paymentType,
      profileId: filters.profileId,
      _forceRefresh: Date.now(),
    }));
  };

  const autoCompletes = [
    {
      label: "Account Profile",
      value: filters.profileId || "",
      options: dataProfiledropdown,
      isDeleted: filterDeleted,
      onToggleDeleted: () => setFilterDeleted((prev) => !prev),
      placeholder: "Select Profile",
      clearable: true,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProfileOption = dataProfiledropdown.find(
          (option) => option.value === value
        );
        setFilters((prev) => ({
          ...prev,
          profileId: String(selectedProfileOption?.value || ""),
        }));
      },
    },
    {
      label: "Account Type",
      value: filters.type || "",
      clearable: true,
      options: [
        { label: "Customer", value: "CLIENT_PAYMENT" },
        { label: "Provider", value: "VENDOR_PAYMENT" },
      ],
      placeholder: "Select Account Type",
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,
          type: value as AccountyEnumType,
        }));
      },
    },
    {
      label: "Payment Type",
      value: filters.paymentType || "",
      options: paymentOptions,
      clearable: true,
      placeholder: "Select Payment Type",
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) {
          console.error("Unexpected array value for Payment Type:", value);
          return;
        }

        setFilters((prev) => ({
          ...prev,
          paymentType: value as PaymentTypeEnum,
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_PAYMENTS",
    },
  ];

  const fields = useMemo<IField<IPayment>[]>(() => [
    {
      key: "profileId",
      header: "Profile",
      transform: async (t) => {
        if (Array.isArray(t.profileId)) {
          const profileNames = t.profileId
            .map((id) => profileMap[id]?.name)
            .join(", ");
          return profileNames;
        }
        const profile = profileMap[String(t.profileId)];
        return profile?.name;
      },
    },
    {
      key: "type",
      header: "Paymnet Type",
      transform: (row) => {
        const formatted = row.type
          ?.toLowerCase()
          .replace(/_/g, " ")
          .replace(/^\w/, (c) => c.toUpperCase());
        return formatted || "";
      },
    },
    {
      key: "invoiceId",
      header: "Invoice Number",
      transform: async (t) => {
        if (!t.invoiceId || !t.type) return "-";
        try {
          if (t.type === "CLIENT_PAYMENT") {
            const bill = await queryClient.fetchQuery({
              queryKey: ["customerBill", t.invoiceId],
              queryFn: () => fetchCustomerBillById(Number(t.invoiceId)),
            });
            return bill?.ref ?? "-";
          } else if (t.type === "VENDOR_PAYMENT") {
            const bill = await queryClient.fetchQuery({
              queryKey: ["providerBill", t.invoiceId],
              queryFn: () => fetchProviderBillById(Number(t.invoiceId)),
            });
            return bill?.ref ?? "-";
          }
          return "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "paymentDate",
      header: "Payment Date",
      transform: (t) => t.paymentDate ? new Date(t.paymentDate).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" }) : "-",
    },
    { key: "paymentType", header: "Payment Type" },
    {
      key: "amount",
      header: "Amount",
      transform: (row) => row.amount.toFixed(3),
    },
    {
      key: "id",
      header: "Currency",
      transform: async (row) => {
        const profileId = Array.isArray(row.profileId)
          ? row.profileId[0]
          : row.profileId;
        if (!profileId) return "-";
        try {
          const profile = await queryClient.fetchQuery({
            queryKey: ["profile", profileId],
            queryFn: () => fetchProfile(String(profileId)),
          });
          if (!profile?.currencyId) return "-";
          const currency = await queryClient.fetchQuery({
            queryKey: ["currency", profile.currencyId],
            queryFn: () => fetchCurrency(Number(profile.currencyId)),
          });
          return currency?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    { key: "ref", header: "Reference Name" },
    { key: "createdDate", header: "Created Date" },
  ], [queryClient, profileMap]);

  // const actions = Actions({
  //   onViewHistory: {
  //     // isDisabled: true,
  //     handler: async (id: string | number) => {
  //       handleOpenDialog("history", String(id));
  //     },
  //   },
  // });
  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Payment List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_PAYMENT",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportPayment(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again."
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_PAYMENTS_CSV",
            },
          ]}
        />

        <CustomTable<IPayment>
          filters={appliedFilters}
          fields={fields}
          // actions={actions}
          fetchFunc={usePaymentQuery}
          sortableFields={["paymentDate", "ref", "amount", "paymentType", "createdDate"]}
        />
      </Box>
      <PaymentDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        profileOptions={profileOptions}
        paymentOptions={paymentOptions}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

interface PaymentDialogRef {
  openDialog: (mode: "add" | "history", id?: string) => void;
}

interface PaymentDialogProps {
  onRefresh: () => void;
  profileOptions: Option[];
  paymentOptions: Option[];
  appliedFilters: Params<IPayment & DateRange>;
}

const PaymentDialog = forwardRef<PaymentDialogRef, PaymentDialogProps>(
  ({ onRefresh, profileOptions, paymentOptions, appliedFilters }, ref) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"add" | "history">("add");
    const [dialogData, setDialogData] = useState<
      Partial<
        IPayment & {
          currencyName: string;
        }
      >
    >({
      createdDate: new Date(),
      id: 0,
      amount: 0,
      ref: "",
      paymentDate: new Date(),
      invoiceId: "",
      profileId: "",
      type: undefined,
    });

    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [invoiceSearch, setInvoiceSearch] = useState("");
    const [selectedInvoiceOption, setSelectedInvoiceOption] =
      useState<Option | null>(null);
    const invoiceSearchTimeoutRef = useRef<number | undefined>(undefined);

    const invoiceSearchParams = invoiceSearch ? { search: invoiceSearch } : {};
    const { data: customerBillData } = useCustomerBillQuery(
      dialogData.type === "CLIENT_PAYMENT" ? invoiceSearchParams : {}
    );
    const { data: providerBillData } = useProviderBillQuery(
      dialogData.type === "VENDOR_PAYMENT" ? invoiceSearchParams : {}
    );

    const invoiceOptions = useMemo<Option[]>(() => {
      const billData =
        dialogData.type === "CLIENT_PAYMENT"
          ? customerBillData
          : dialogData.type === "VENDOR_PAYMENT"
            ? providerBillData
            : undefined;
      // Reuse the selected option's object so MUI doesn't treat new search results as a value change and reset the typed input
      const options = (billData?.content ?? []).map((bill) =>
        selectedInvoiceOption && String(bill.id) === selectedInvoiceOption.value
          ? selectedInvoiceOption
          : { label: bill.ref, value: String(bill.id) }
      );
      if (selectedInvoiceOption && !options.includes(selectedInvoiceOption)) {
        return [selectedInvoiceOption, ...options];
      }
      return options;
    }, [dialogData.type, customerBillData, providerBillData, selectedInvoiceOption]);

    const resetInvoiceSearch = () => {
      window.clearTimeout(invoiceSearchTimeoutRef.current);
      setInvoiceSearch("");
    };

    const handleInvoiceInputChange = (
      _event: React.SyntheticEvent,
      value: string,
      reason?: string
    ) => {
      if (reason !== "input") {
        resetInvoiceSearch();
        return;
      }
      window.clearTimeout(invoiceSearchTimeoutRef.current);
      invoiceSearchTimeoutRef.current = window.setTimeout(() => {
        setInvoiceSearch(value.trim());
      }, 200);
    };

    useEffect(() => {
      return () => window.clearTimeout(invoiceSearchTimeoutRef.current);
    }, []);

    const { data: AccountOptions } = useAccountsQuery({
      profileId: dialogData.profileId,
      type: dialogData.type === "CLIENT_PAYMENT" ? "CLIENT" : "VENDOR",
    });
    const accountOptionsList: Option[] = (AccountOptions?.content ?? []).map(
      (account) => ({
        label: account.name,
        value: account.id,
      })
    );

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setDialogOpen(true);
      },
    }));

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useEffect(() => {
      const isAnyFieldEmpty =
        !dialogData.ref ||
        !dialogData.amount ||
        !dialogData.profileId ||
        !dialogData.paymentDate ||
        !dialogData.paymentType ||
        !dialogData.type;
      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    useEffect(() => {
      if (dialogMode === "add") {
        openDialog("add");
      }
    }, [dialogMode]);

    const openDialog = async (mode: "add", id?: string) => {
      setDialogMode(mode);

      try {
        setDialogData({});
        setSelectedInvoiceOption(null);
        resetInvoiceSearch();
      } catch (error: any) {
        console.error(`Error fetching Payment data for ${mode} mode:`, error);
      }
    };

    const closeDialog = () => setDialogOpen(false);

    const addPayment = async (data: Partial<IPayment>) => {
      try {
        await addPaymentApi({
          ...data,
          connectionProfileIds: dialogData.connectionProfileIds,
          invoiceId: dialogData.invoiceId || undefined,
        });
        onRefresh();
        closeDialog();
        setDialogData({});

        ToastNotification.success("Payment added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const isValidAccountyEnumType = (value: any): value is AccountyEnumType => {
      return value === "CLIENT_PAYMENT" || value === "VENDOR_PAYMENT";
    };

    const fieldHistory = useMemo<IField<IPayment>[]>(() => [
      // {
      //   key: "profileId",
      //   header: "Profile",
      //   transform: async (t) => {
      //     if (Array.isArray(t.profileId)) {
      //       const profileNames = t.profileId
      //         .map((id) => profileMap[id]?.name)
      //         .join(", ");
      //       return profileNames;
      //     }
      //     const profile = profileMap[String(t.profileId)];
      //     return profile?.name;
      //   },
      // },
      {
        key: "type",
        header: "Paymnet Type",
        transform: (row) => {
          const formatted = row.type
            ?.toLowerCase()
            .replace(/_/g, " ")
            .replace(/^\w/, (c) => c.toUpperCase());
          return formatted || "";
        },
      },
      {
        key: "invoiceId",
        header: "Invoice Number",
        transform: async (t) => {
          if (!t.invoiceId || !t.type) return "-";
          try {
            if (t.type === "CLIENT_PAYMENT") {
              const bill = await queryClient.fetchQuery({
                queryKey: ["customerBill", t.invoiceId],
                queryFn: () => fetchCustomerBillById(Number(t.invoiceId)),
              });
              return bill?.ref ?? "-";
            } else if (t.type === "VENDOR_PAYMENT") {
              const bill = await queryClient.fetchQuery({
                queryKey: ["providerBill", t.invoiceId],
                queryFn: () => fetchProviderBillById(Number(t.invoiceId)),
              });
              return bill?.ref ?? "-";
            }
            return "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "paymentDate",
        header: "Payment Date",
        transform: (t) => t.paymentDate ? new Date(t.paymentDate).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" }) : "-",
      },
      { key: "paymentType", header: "Payment Type" },
      {
        key: "amount",
        header: "Amount",
        transform: (row) => row.amount.toFixed(3),
      },
      {
        key: "id",
        header: "Currency",
        transform: async (row) => {
          const profileId = Array.isArray(row.profileId)
            ? row.profileId[0]
            : row.profileId;
          if (!profileId) return "-";
          try {
            const profile = await queryClient.fetchQuery({
              queryKey: ["profile", profileId],
              queryFn: () => fetchProfile(String(profileId)),
            });
            if (!profile?.currencyId) return "-";
            const currency = await queryClient.fetchQuery({
              queryKey: ["currency", profile.currencyId],
              queryFn: () => fetchCurrency(Number(profile.currencyId)),
            });
            return currency?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "ref", header: "Reference Name" },
      { key: "updatedBy", header: "Updated By" },
    ], [queryClient]);

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        mode={dialogMode}
        dialogSx={{ minWidth: 800 }}
        title={dialogMode === "history" ? "History" : "Add New Payment"}
        fieldsPerRow={2}
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History<IPayment>
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={usePaymentQuery}
                    />
                  ),
                },
              ]
            : [
                {
                  type: "custom",
                  highlightLabelStar: true,
                  component: (
                    <CustomDateTimePicker
                      label="Payment Date"
                      WitOutTime
                      value={
                        dialogData.paymentDate
                          ? new Date(dialogData.paymentDate)
                          : null
                      }
                      highlightLabelStar
                      onChange={(date: Date | null) => {
                        setDialogData((prev) => ({
                          ...prev,
                          paymentDate: date ?? undefined,
                        }));
                        validateField(
                          "paymentDate",
                          date ? date.toISOString() : ""
                        );
                      }}
                      error={!!errors.paymentDate}
                      helperText={
                        errors.paymentDate ? "Payment Date is required" : ""
                      }
                    />
                  ),
                },
                {
                  type: "text",
                  typeinput: "number",
                  label: "Amount",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value:
                    dialogData.amount !== undefined ? dialogData.amount : "",
                  onChange: (value) => {
                    const numericValue =
                      value === "" ? undefined : Number(value);
                    setDialogData((prev) => ({
                      ...prev,
                      amount: numericValue,
                    }));
                  },
                  placeholder: "Amount",
                  error: !!errors.cost,
                  helperText: errors.cost ? "Amount is required" : "",
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Account Profile"
                      value={dialogData.profileId || ""}
                      options={profileOptions}
                      onChange={async (
                        value: string | number | (string | number)[]
                      ) => {
                        const selectedProfileId = String(value);
                        setDialogData((prev) => ({
                          ...prev,
                          profileId: selectedProfileId,
                        }));
                        validateField("selectedProfileId", selectedProfileId);

                        // Only fetch profile and currency if a value is selected
                        if (selectedProfileId && selectedProfileId !== "") {
                          try {
                            const profile = await fetchProfile(
                              selectedProfileId
                            );
                            const currency = await fetchCurrency(
                              profile.currencyId
                            );
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: currency.name,
                            }));
                          } catch (error: any) {
                            console.error(
                              "Error fetching currency for selected provider:",
                              error
                            );
                            ToastNotification.error(error.message);
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: "",
                            }));
                          }
                        } else {
                          // Clear currency name when profile is cleared
                          setDialogData((prev) => ({
                            ...prev,
                            currencyName: "",
                          }));
                        }
                      }}
                      clearable={true}
                      disablePortal={false}
                      error={!!errors.accountProfile}
                      placeholder="Select Account Profile"
                      highlightLabelStar={true}
                      helperText={
                        errors.accountProfile
                          ? "Account Profile is required"
                          : ""
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Currency",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: false,
                  value: dialogData.currencyName || "",
                  disabled: true,
                  onChange: (value) => {},
                  placeholder: "Existing Currency",
                },
                {
                  type: "select",
                  label: "Payment Type",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  options: paymentOptions,
                  value: dialogData.paymentType || "",
                  onChange: (value: string | number | (string | number)[]) => {
                    setDialogData((prev) => ({
                      ...prev,
                      paymentType: value as PaymentTypeEnum,
                    }));

                    validateField("paymentType", String(value));
                  },
                  placeholder: "Select Payment Type",
                  error: !!errors.paymentType,
                  helperText: errors.paymentType
                    ? "Payment Type is required"
                    : "",
                },
                {
                  type: "select",
                  label: "Account Type",
                  placeholder: "Select Account Type",
                  options: [
                    { label: "Customer", value: "CLIENT_PAYMENT" },
                    { label: "Provider", value: "VENDOR_PAYMENT" },
                  ],
                  renderPlaceholder: true,
                  value: dialogData.type || "",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  error: errors.type || false,
                  helperText: errors.type ? "This field is required." : "",
                  onChange: async (
                    value: string | number | (string | number)[]
                  ) => {
                    const selectedValue = String(value);

                    if (!isValidAccountyEnumType(selectedValue)) {
                      console.error(`Invalid account type: ${selectedValue}`);
                      ToastNotification.error("Invalid account type selected.");
                      return;
                    }

                    setDialogData((prev) => ({
                      ...prev,
                      type: selectedValue,
                      invoiceId: "",
                    }));
                    setSelectedInvoiceOption(null);
                    resetInvoiceSearch();
                  },
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Invoice"
                      value={dialogData.invoiceId || ""}
                      options={invoiceOptions}
                      onChange={(value: string | number | (string | number)[]) => {
                        setDialogData((prev) => ({
                          ...prev,
                          invoiceId: String(value) ?? undefined,
                        }));
                        setSelectedInvoiceOption(
                          invoiceOptions.find(
                            (option) => option.value === String(value)
                          ) ?? null
                        );
                      }}
                      onInputChange={handleInvoiceInputChange}
                      clearable={true}
                      placeholder="Select Invoice"
                      disabled={!dialogData.type}
                      disablePortal={false}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Reference Name",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.ref || "",
                  onChange: (value: string | number) => {
                    setDialogData((prev) => ({
                      ...prev,
                      ref: String(value),
                    }));
                  },
                  placeholder: "Enter Reference Name",
                  error: !!errors.ref,
                  helperText: errors.ref ? "Reference Name is required" : "",
                },
              ]
        }
        buttons={[
          {
            label: "Save",
            variant: "contained" as "contained",
            bgColor: COLORS.GOLD,
            borderRadius: "12px",
            onClick: async () => {
              await addPayment(dialogData);
            },
            disabled: isSaveDisabled || !hasJwtPermission("POST_PAYMENT"),
          },
        ]}
      />
    );
  }
);

export default PaymentPage;
